import { Pod } from "../store/slices/podsSlice";
import {
  Epic,
  Sprint,
  SprintStatus,
} from "../store/slices/epicsSlice";
import type {
  Comment,
  CommentReaction,
  CommentReply,
  HistoryEntry,
  HistoryChange,
  ReactionEmoji,
  SprintHistoryEntry,
} from "../store/slices/sharedTypes";
import { Story } from "../store/slices/storiesSlice";
import {
  Workstream,
  WorkstreamEntry,
  DEFAULT_WORKSTREAM_STATES,
} from "../store/slices/workstreamsSlice";
import { WorkstreamSprint } from "../store/slices/sprintsSlice";
import { Category } from "../store/slices/categoriesSlice";



// ---------------------------------------------------------------------------
// Demo comment / history helpers
// ---------------------------------------------------------------------------

/** Known demo team members used as comment authors in the demo dataset. */
const DEMO_AUTHORS = {
  rachelTorres: { id: "sf-pm1",  name: "Rachel Torres" },
  marcusLee:    { id: "sf-d1",   name: "Marcus Lee" },
  anikaPatel:   { id: "sf-e1",   name: "Anika Patel" },
  jordanKim:    { id: "sf-e2",   name: "Jordan Kim" },
  devonWalsh:   { id: "sf-e5",   name: "Devon Walsh" },
  chrisAndrade: { id: "sf-e7",   name: "Chris Andrade" },
  ethanRivera:  { id: "cp-pm1",  name: "Ethan Rivera" },
  leilaNguyen:  { id: "cp-d1",   name: "Leila Nguyen" },
  sofiaHernandez:{ id: "cp-e1",  name: "Sofia Hernandez" },
  isabelleGrant: { id: "cp-e3",  name: "Isabelle Grant" },
  zoeYamamoto:  { id: "cp-e5",   name: "Zoe Yamamoto" },
  alexChen:     { id: "ic-e1",   name: "Alex Chen" },
  dianaFlores:  { id: "ic-pm1",  name: "Diana Flores" },
  currentUser:  { id: "current-user", name: "You" },
} as const;

/** Build a Comment object with sensible defaults. */
function makeComment(
  id: string,
  author: { id: string; name: string },
  content: string,
  createdAt: string,
  opts?: {
    updatedAt?: string;
    reactions?: Array<{ emoji: ReactionEmoji; userIds: string[] }>;
    replies?: CommentReply[];
  },
): Comment {
  return {
    id,
    authorId: author.id,
    authorName: author.name,
    content,
    createdAt,
    updatedAt: opts?.updatedAt,
    reactions: (opts?.reactions ?? []) as CommentReaction[],
    replies: opts?.replies ?? [],
  };
}

/** Build a HistoryEntry object. */
function makeHistory(
  id: string,
  changedBy: string,
  timestamp: string,
  changes: HistoryChange[],
): HistoryEntry {
  return { id, timestamp, changedBy, changes };
}

// Demo data for an e-commerce platform that sells vehicles
// Three pods, each with 4 engineers (plus PM and Designer)
// Category-to-pod assignments: Storefront=Feature+UX, Checkout=Integration, Inventory=Infrastructure

export const scenario1Pods: Pod[] = [
  {
    id: "demo-pod-storefront",
    name: "Storefront",
    description:
      "Owns the customer-facing vehicle browsing and search experience.",
    defaultVelocity: 20,
    avgPointsPerEngineer: 5,
    teamMembers: [
      {
        id: "sf-pm1",
        name: "Rachel Torres",
        group: "Product",
        practice: "Product Management",
      },
      {
        id: "sf-d1",
        name: "Marcus Lee",
        group: "Design",
        practice: "UX Design",
      },
      {
        id: "sf-e1",
        name: "Anika Patel",
        group: "Engineering",
        practice: "Frontend",
      },
      {
        id: "sf-e2",
        name: "Jordan Kim",
        group: "Engineering",
        practice: "Frontend",
      },
      {
        id: "sf-e5",
        name: "Devon Walsh",
        group: "Engineering",
        practice: "Backend",
      },
      {
        id: "sf-e7",
        name: "Chris Andrade",
        group: "Engineering",
        practice: "Full Stack",
      },
    ],
  },
  {
    id: "demo-pod-checkout",
    name: "Checkout & Payments",
    description:
      "Handles the end-to-end purchase flow, financing, and payment processing.",
    defaultVelocity: 18,
    avgPointsPerEngineer: 5,
    teamMembers: [
      {
        id: "cp-pm1",
        name: "Ethan Rivera",
        group: "Product",
        practice: "Product Management",
      },
      {
        id: "cp-d1",
        name: "Leila Nguyen",
        group: "Design",
        practice: "UX Design",
      },
      {
        id: "cp-e1",
        name: "Sofia Hernandez",
        group: "Engineering",
        practice: "Frontend",
      },
      {
        id: "cp-e3",
        name: "Isabelle Grant",
        group: "Engineering",
        practice: "Backend",
      },
      {
        id: "cp-e5",
        name: "Zoe Yamamoto",
        group: "Engineering",
        practice: "Backend",
      },
      {
        id: "cp-e7",
        name: "Brianna Scott",
        group: "Engineering",
        practice: "Full Stack",
      },
    ],
  },
  {
    id: "demo-pod-inventory",
    name: "Inventory & Catalog",
    description:
      "Manages vehicle inventory ingestion, catalog enrichment, and dealer portal integrations.",
    defaultVelocity: 19,
    avgPointsPerEngineer: 5,
    teamMembers: [
      {
        id: "ic-pm1",
        name: "Diana Flores",
        group: "Product",
        practice: "Product Management",
      },
      {
        id: "ic-d1",
        name: "Raj Mehta",
        group: "Design",
        practice: "UX Design",
      },
      {
        id: "ic-e1",
        name: "Alex Chen",
        group: "Engineering",
        practice: "Backend",
      },
      {
        id: "ic-e4",
        name: "Nadia Petrov",
        group: "Engineering",
        practice: "Data Engineering",
      },
      {
        id: "ic-e5",
        name: "Carlos Reyes",
        group: "Engineering",
        practice: "Data Engineering",
      },
      {
        id: "ic-e8",
        name: "Yuki Tanaka",
        group: "Engineering",
        practice: "DevOps",
      },
    ],
  },
];

export const scenario1Categories: Category[] = [
  { id: "demo-cat-feature", name: "Feature", color: "#4caf50" },
  { id: "demo-cat-infrastructure", name: "Infrastructure", color: "#2196f3" },
  { id: "demo-cat-ux", name: "UX Improvement", color: "#9c27b0" },
  { id: "demo-cat-integration", name: "Integration", color: "#ff9800" },
];

export const scenario1Epics: Epic[] = [
  {
    id: "demo-epic-1",
    title: "Vehicle Listing & Browse Experience",
    description:
      "Build the core vehicle listing page with grid/list toggle, pagination, and summary cards showing key specs (make, model, year, price, mileage). Includes lazy-loading images and accessibility improvements.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 1,
    capacity: { Engineering: 34 },
    parentState: 'Closed',
    status: 'Completed',
    history: [
      makeHistory(
        "h-epic1-3",
        DEMO_AUTHORS.rachelTorres.name,
        "2026-03-14T08:30:00.000Z",
        [{ field: "Description", from: "Build the core vehicle listing page with grid/list toggle and pagination.", to: "Build the core vehicle listing page with grid/list toggle, pagination, and summary cards showing key specs (make, model, year, price, mileage). Includes lazy-loading images and accessibility improvements." }],
      ),
      makeHistory(
        "h-epic1-2",
        DEMO_AUTHORS.rachelTorres.name,
        "2026-03-07T16:45:00.000Z",
        [
          { field: "Category", from: "(none)", to: "Feature" },
          { field: "Pod", from: "Checkout & Payments", to: "Storefront" },
        ],
      ),
      makeHistory(
        "h-epic1-1",
        DEMO_AUTHORS.ethanRivera.name,
        "2026-03-03T11:00:00.000Z",
        [{ field: "Title", from: "Listing & Browse", to: "Vehicle Listing & Browse Experience" }],
      ),
    ],
    comments: [
      makeComment(
        "c-epic1-1",
        DEMO_AUTHORS.rachelTorres,
        "<p>Kicking off this epic — here's a quick summary of the <strong>scope</strong> we agreed in last week's planning session:</p><ul><li>Grid &amp; list toggle with persisted preference</li><li>Summary cards with lazy-loading images</li><li>Pagination (URL-synced)</li><li>WCAG 2.1 AA accessibility</li></ul><p>The stretch goal for the 360° preview is <em>not</em> in scope for this iteration — that's captured in Epic 3.</p>",
        "2026-03-03T11:30:00.000Z",
        {
          reactions: [
            { emoji: "👍", userIds: [DEMO_AUTHORS.anikaPatel.id, DEMO_AUTHORS.jordanKim.id, DEMO_AUTHORS.devonWalsh.id] },
            { emoji: "🎉", userIds: [DEMO_AUTHORS.marcusLee.id] },
          ],
        },
      ),
      makeComment(
        "c-epic1-2",
        DEMO_AUTHORS.marcusLee,
        "<p>Designs are ready in Figma for review. I've covered all three breakpoints (mobile/tablet/desktop) and both grid and list variants. Key thing to call out: on mobile the price moves <em>below</em> the image, not inline with specs — please check the handoff notes for exact spacing tokens.</p>",
        "2026-03-05T14:10:00.000Z",
        {
          reactions: [
            { emoji: "🔥", userIds: [DEMO_AUTHORS.anikaPatel.id, DEMO_AUTHORS.jordanKim.id] },
          ],
          replies: [
            {
              id: "r-epic1-2-1",
              authorId: DEMO_AUTHORS.anikaPatel.id,
              authorName: DEMO_AUTHORS.anikaPatel.name,
              content: "Great work Marcus — I'll start implementation from the mobile designs first. Will ping you if I spot any gaps.",
              createdAt: "2026-03-05T15:00:00.000Z",
            },
          ],
        },
      ),
      makeComment(
        "c-epic1-3",
        DEMO_AUTHORS.currentUser,
        "<p>Reminder: the accessibility story (e1-e5) should be reviewed by our a11y specialist before it's marked done. I've added a note to the story acceptance criteria. @Rachel can you confirm who the right reviewer is?</p>",
        "2026-03-11T09:00:00.000Z",
        {
          reactions: [{ emoji: "👍", userIds: [DEMO_AUTHORS.rachelTorres.id] }],
          replies: [
            {
              id: "r-epic1-3-1",
              authorId: DEMO_AUTHORS.rachelTorres.id,
              authorName: DEMO_AUTHORS.rachelTorres.name,
              content: "Good call — reach out to Jordan Kim who is our designated a11y champion this quarter.",
              createdAt: "2026-03-11T09:45:00.000Z",
            },
          ],
        },
      ),
    ],
  },
  {
    id: "demo-epic-2",
    title: "Advanced Search & Filtering",
    description:
      "Implement faceted search with filters for make, model, year range, price range, mileage, fuel type, transmission, and body style. Persist filter state in the URL for shareable search links.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 2,
    capacity: { Engineering: 28 },
    parentState: 'Closed',
    status: 'Completed',
    history: [
      makeHistory(
        "h-epic2-2",
        DEMO_AUTHORS.rachelTorres.name,
        "2026-03-10T10:00:00.000Z",
        [{ field: "Story Points", from: "24", to: "28" }],
      ),
      makeHistory(
        "h-epic2-1",
        DEMO_AUTHORS.rachelTorres.name,
        "2026-03-04T09:30:00.000Z",
        [{ field: "Title", from: "Search & Filtering", to: "Advanced Search & Filtering" }],
      ),
    ],
    comments: [
      makeComment(
        "c-epic2-1",
        DEMO_AUTHORS.rachelTorres,
        "<p>Heads-up that the <strong>URL state persistence</strong> story is a prerequisite for the SEO work in Epic 13. Let's make sure it ships in the same sprint as the listing page so the URLs are indexable from day one.</p>",
        "2026-03-04T10:00:00.000Z",
        {
          reactions: [{ emoji: "👍", userIds: [DEMO_AUTHORS.devonWalsh.id, DEMO_AUTHORS.anikaPatel.id] }],
        },
      ),
      makeComment(
        "c-epic2-2",
        DEMO_AUTHORS.jordanKim,
        "<p>The filter panel design has a mobile-first accordion treatment. On desktop it expands into a sidebar. I've already spiked the responsive logic — happy to share the branch if anyone wants a preview before designs are finalized.</p>",
        "2026-03-06T13:00:00.000Z",
        {
          reactions: [
            { emoji: "🔥", userIds: [DEMO_AUTHORS.rachelTorres.id] },
            { emoji: "🎉", userIds: [DEMO_AUTHORS.marcusLee.id] },
          ],
          replies: [
            {
              id: "r-epic2-2-1",
              authorId: DEMO_AUTHORS.marcusLee.id,
              authorName: DEMO_AUTHORS.marcusLee.name,
              content: "Yes please share the branch! I want to confirm the accordion animation matches the design system token.",
              createdAt: "2026-03-06T13:45:00.000Z",
            },
            {
              id: "r-epic2-2-2",
              authorId: DEMO_AUTHORS.currentUser.id,
              authorName: DEMO_AUTHORS.currentUser.name,
              content: "Linking the branch in our Slack thread too.",
              createdAt: "2026-03-06T14:00:00.000Z",
            },
          ],
        },
      ),
    ],
  },
  {
    id: "demo-epic-3",
    title: "Vehicle Detail Page",
    description:
      'Create a rich vehicle detail page with a photo gallery carousel, 360° spin viewer, full specification table, vehicle history report integration, and a sticky "Request a Quote" CTA panel.',
    categoryId: "demo-cat-ux",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 3,
    capacity: { Engineering: 30 },
    parentState: 'In Progress',
    status: 'In Progress',
  },
  {
    id: "demo-epic-4",
    title: "Shopping Cart & Saved Vehicles",
    description:
      "Allow buyers to save vehicles to a wishlist and add them to a cart for price comparison. Persist saved state across sessions via user accounts and support anonymous guest sessions.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 4,
    capacity: { Engineering: 24 },
    parentState: 'In Progress',
    status: 'Not Started',
  },
  {
    id: "demo-epic-5",
    title: "Checkout & Payment Processing",
    description:
      "End-to-end checkout flow covering buyer information, trade-in valuation, down payment, payment method selection (credit card, ACH, third-party finance), and order confirmation with e-signature.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 5,
    capacity: { Engineering: 40 },
    parentState: 'In Progress',
    status: 'Blocked',
    history: [
      makeHistory(
        "h-epic5-2",
        DEMO_AUTHORS.ethanRivera.name,
        "2026-03-12T15:00:00.000Z",
        [
          { field: "Description", from: "End-to-end checkout flow.", to: "End-to-end checkout flow covering buyer information, trade-in valuation, down payment, payment method selection (credit card, ACH, third-party finance), and order confirmation with e-signature." },
        ],
      ),
      makeHistory(
        "h-epic5-1",
        DEMO_AUTHORS.ethanRivera.name,
        "2026-03-05T09:00:00.000Z",
        [
          { field: "Pod", from: "Storefront", to: "Checkout & Payments" },
          { field: "Category", from: "Infrastructure", to: "Feature" },
        ],
      ),
    ],
    comments: [
      makeComment(
        "c-epic5-1",
        DEMO_AUTHORS.ethanRivera,
        "<p>This epic is <strong>critical path</strong> for the Q2 revenue target. The payment gateway and e-signature stories need to be delivered together to complete the first full purchase loop. Please flag any blockers immediately — I'm checking in daily on this one.</p>",
        "2026-03-05T09:30:00.000Z",
        {
          reactions: [
            { emoji: "👍", userIds: [DEMO_AUTHORS.isabelleGrant.id, DEMO_AUTHORS.zoeYamamoto.id, DEMO_AUTHORS.sofiaHernandez.id] },
          ],
        },
      ),
      makeComment(
        "c-epic5-2",
        DEMO_AUTHORS.sofiaHernandez,
        "<p>Just flagging a dependency: the multi-step checkout UI (e5-e4) needs the <strong>checkout session API</strong> (e5-e1) to be complete first, otherwise I can only build against mocked data. Can we prioritize e5-e1 for the first half of the sprint?</p>",
        "2026-03-08T10:15:00.000Z",
        {
          reactions: [{ emoji: "👍", userIds: [DEMO_AUTHORS.ethanRivera.id] }],
          replies: [
            {
              id: "r-epic5-2-1",
              authorId: DEMO_AUTHORS.ethanRivera.id,
              authorName: DEMO_AUTHORS.ethanRivera.name,
              content: "Confirmed — e5-e1 is now the first story assigned this sprint. Isabelle starts Monday.",
              createdAt: "2026-03-08T11:00:00.000Z",
            },
            {
              id: "r-epic5-2-2",
              authorId: DEMO_AUTHORS.currentUser.id,
              authorName: DEMO_AUTHORS.currentUser.name,
              content: "I'll create a shared Postman collection for the checkout session API so the FE team can mock against it immediately.",
              createdAt: "2026-03-08T11:30:00.000Z",
            },
          ],
        },
      ),
      makeComment(
        "c-epic5-3",
        DEMO_AUTHORS.isabelleGrant,
        "<p>DocuSign embedded signing (e5-e5) requires us to be on the <strong>Business Pro</strong> plan or higher. I've raised this with procurement — should have confirmation by EOD Thursday. If it's a blocker we have a fallback option using a simple checkbox consent pattern.</p>",
        "2026-03-11T14:00:00.000Z",
        {
          reactions: [
            { emoji: "🔥", userIds: [DEMO_AUTHORS.ethanRivera.id] },
          ],
          replies: [
            {
              id: "r-epic5-3-1",
              authorId: DEMO_AUTHORS.ethanRivera.id,
              authorName: DEMO_AUTHORS.ethanRivera.name,
              content: "Let's proceed with the checkbox fallback in parallel so we're not blocked. We can swap to DocuSign when procurement confirms.",
              createdAt: "2026-03-11T14:30:00.000Z",
            },
          ],
        },
      ),
    ],
  },
  {
    id: "demo-epic-6",
    title: "Vehicle Financing Calculator",
    description:
      "Interactive financing tool that calculates monthly payments based on purchase price, down payment, loan term, and estimated interest rate. Integrate with multiple lender APIs for real-time pre-qualification.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 3,
    capacity: { Engineering: 22 },
    parentState: 'In Progress',
    status: 'In Progress',
  },
  {
    id: "demo-epic-7",
    title: "User Account & Order History",
    description:
      "Build user registration, login (including OAuth via Google/Apple), profile management, and a dashboard showing purchase history, saved searches, and upcoming delivery milestones.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 6,
    capacity: { Engineering: 20 },
    parentState: 'In Progress',
    status: 'Not Started',
  },
  {
    id: "demo-epic-8",
    title: "Dealer Inventory Management Portal",
    description:
      "Self-service portal for dealers to upload, update, and deactivate vehicle listings via CSV bulk import or manual entry. Includes real-time inventory sync, listing preview, and performance analytics.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 1,
    capacity: { Engineering: 32 },
    parentState: 'In Progress',
    status: 'In Progress',
  },
  {
    id: "demo-epic-9",
    title: "Vehicle Catalog Enrichment Pipeline",
    description:
      "Automated data pipeline that normalizes raw dealer feeds, appends missing specs from industry databases (NHTSA, VIN decode), generates SEO-optimized descriptions, and flags data quality issues.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 2,
    capacity: { Engineering: 28 },
    parentState: 'Closed',
    status: 'Completed',
  },
  {
    id: "demo-epic-10",
    title: "Search Index & Performance Infrastructure",
    description:
      "Design and build a scalable Elasticsearch-based search index for the vehicle catalog. Support near real-time index updates as inventory changes and optimize query performance for sub-100ms response times.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 3,
    capacity: { Engineering: 30 },
    parentState: 'In Progress',
    status: 'In Progress',
  },
  {
    id: "demo-epic-11",
    title: "Personalized Vehicle Recommendations",
    description:
      "ML-powered recommendation engine that surfaces relevant vehicles based on browsing history, saved searches, and demographic data. A/B test placement on the home page and vehicle detail pages.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 9,
    capacity: { Engineering: 26 },
    parentState: 'Open',
    status: 'Ready',
  },

  // ── Storefront Pod – additional epics (priorities 4–12) ──────────────────
  {
    id: "demo-epic-12",
    title: "Homepage Hero & Promotions Banner",
    description:
      "Build a configurable homepage hero section with rotating promotional banners, featured vehicle spotlights, and seasonal campaign support managed via a CMS.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 4,
    capacity: { Engineering: 20 },
    parentState: 'In Progress',
    status: 'Not Started',
  },
  {
    id: "demo-epic-13",
    title: "SEO & Metadata Optimization",
    description:
      "Improve organic search ranking by implementing structured data (JSON-LD), dynamic Open Graph tags, canonical URLs, sitemap generation, and server-side rendering for key listing pages.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 4,
    capacity: { Engineering: 18 },
    storyPointOverrides: { Engineering: 18 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-14",
    title: "Vehicle Comparison Tool",
    description:
      "Side-by-side comparison of up to four vehicles highlighting differences in price, mileage, specs, and features. Accessible from listing and detail pages with persistent comparison tray.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 6,
    capacity: { Engineering: 22 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-15",
    title: "Mobile Experience Redesign",
    description:
      "Redesign the core browsing and search experience for mobile-first interactions, including touch-optimized filters, swipeable carousels, and bottom navigation patterns.",
    categoryId: "demo-cat-ux",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 7,
    capacity: { Engineering: 24 },
    storyPointOverrides: { Engineering: 24 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-16",
    title: "Customer Reviews & Ratings",
    description:
      "Allow verified buyers to leave star ratings and written reviews on vehicle listings and dealer profiles. Surface aggregate ratings in search results and detail pages.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 8,
    capacity: { Engineering: 18 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-17",
    title: "Dealer Locator & Map Integration",
    description:
      "Interactive map experience allowing buyers to find nearby dealerships, view contact details, check inventory availability, and request test drives from the storefront.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 5,
    capacity: { Engineering: 16 },
    storyPointOverrides: { Engineering: 16 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-18",
    title: "Vehicle Price Drop Alerts",
    description:
      "Allow buyers to subscribe to price drop notifications on saved vehicles. Deliver alerts via email and in-app push with configurable threshold settings.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 10,
    capacity: { Engineering: 14 },
    storyPointOverrides: { Engineering: 14 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-19",
    title: "Storefront Performance & Core Web Vitals",
    description:
      "Achieve Lighthouse scores above 90 on all core pages by addressing LCP, CLS, and FID issues. Implement CDN caching, image optimisation, and code-splitting strategies.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 5,
    capacity: { Engineering: 20 },
    storyPointOverrides: { Engineering: 20 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-20",
    title: "Storefront Analytics & Event Tracking",
    description:
      "Implement a comprehensive analytics layer covering page views, search interactions, CTA clicks, and funnel drop-offs. Integrate with Google Analytics 4 and an internal data warehouse.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 6,
    capacity: { Engineering: 16 },
    storyPointOverrides: { Engineering: 16 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },

  // ── Checkout & Payments Pod – additional epics (priorities 5–12) ─────────
  {
    id: "demo-epic-21",
    title: "Tax, Fees & Compliance Calculator",
    description:
      "Dynamically calculate state and local taxes, registration fees, documentation fees, and dealer add-ons during checkout. Ensure compliance with jurisdiction-specific rules.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 7,
    capacity: { Engineering: 20 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-22",
    title: "Trade-In Vehicle Valuation",
    description:
      "Allow buyers to receive an instant trade-in valuation for their existing vehicle during checkout. Integrate with a third-party appraisal API and apply the value as a down payment credit.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 6,
    capacity: { Engineering: 22 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-23",
    title: "Delivery Scheduling & Logistics",
    description:
      "Enable buyers to schedule home delivery or dealer pickup after purchase. Coordinate with logistics providers for delivery slot availability and send status updates.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 7,
    capacity: { Engineering: 24 },
    storyPointOverrides: { Engineering: 24 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-24",
    title: "Post-Purchase Notifications & Communications",
    description:
      "Automated email and SMS notifications covering order confirmation, document signing reminders, delivery updates, and post-delivery follow-up surveys.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 8,
    capacity: { Engineering: 14 },
    storyPointOverrides: { Engineering: 14 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-25",
    title: "Refund & Cancellation Flow",
    description:
      "Build a self-service cancellation and refund process for buyers within the permitted window. Integrate with the payment gateway for automated refund issuance and update inventory status.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 10,
    capacity: { Engineering: 18 },
    storyPointOverrides: { Engineering: 18 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-26",
    title: "Fraud Detection & Risk Scoring",
    description:
      "Integrate a fraud detection service to score high-value transactions in real time. Flag suspicious orders for manual review and block confirmed fraudulent payments before fulfilment.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 7,
    capacity: { Engineering: 20 },
    storyPointOverrides: { Engineering: 20 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-27",
    title: "Checkout Funnel Analytics",
    description:
      "Instrument every checkout step with granular event tracking to measure drop-off rates, identify friction points, and support data-driven optimisation experiments.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 8,
    capacity: { Engineering: 12 },
    storyPointOverrides: { Engineering: 12 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-28",
    title: "Buy Now Pay Later Integration",
    description:
      "Add BNPL payment options (e.g. Affirm, Klarna) as an alternative payment method at checkout. Surface pre-qualification offers and integrate with the existing payment gateway flow.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 12,
    capacity: { Engineering: 16 },
    storyPointOverrides: { Engineering: 16 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },

  // ── Inventory & Catalog Pod – additional epics (priorities 5–12) ─────────
  {
    id: "demo-epic-29",
    title: "Photo & Media Asset Management",
    description:
      "Build a centralised media asset pipeline that ingests, validates, transforms, and stores vehicle photos and videos. Automatically generate responsive image variants and enforce brand quality standards.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 5,
    capacity: { Engineering: 24 },
    parentState: 'Open',
    status: 'Ready',
  },
  {
    id: "demo-epic-30",
    title: "Pricing Engine & Market Analytics",
    description:
      "Build a pricing intelligence engine that benchmarks vehicle prices against market data, flags over/under-priced listings, and provides dealers with pricing recommendations.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 11,
    capacity: { Engineering: 26 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-31",
    title: "Data Governance & Compliance",
    description:
      "Implement data retention policies, PII masking, GDPR/CCPA right-to-erasure workflows, and an audit log for all catalog data mutations to satisfy regulatory requirements.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 7,
    capacity: { Engineering: 20 },
    storyPointOverrides: { Engineering: 20 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-32",
    title: "Multi-Region Inventory Support",
    description:
      "Extend the catalog and search infrastructure to partition inventory by geographic region, enabling region-specific pricing, availability, and regulatory compliance rules.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 8,
    capacity: { Engineering: 22 },
    storyPointOverrides: { Engineering: 22 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-33",
    title: "Catalog API for Third-Party Partners",
    description:
      "Expose a versioned public API for approved third-party partners to query the vehicle catalog. Implement API key management, rate limiting, and partner usage analytics.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 6,
    capacity: { Engineering: 18 },
    storyPointOverrides: { Engineering: 18 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-34",
    title: "Vehicle Condition Reporting",
    description:
      "Enable dealers to submit standardized vehicle condition reports including inspection photos and defect annotations. Surface condition grade badges on listing and detail pages.",
    categoryId: "demo-cat-feature",
    podId: "demo-pod-storefront",
    workstreamId: "workstream-1",
    priority: 12,
    capacity: { Engineering: 16 },
    storyPointOverrides: { Engineering: 16 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-35",
    title: "Inventory Forecasting & Reporting",
    description:
      "Build internal reporting dashboards for inventory turnover, days-on-lot, supply by category, and demand forecasting to guide merchandising and procurement decisions.",
    categoryId: "demo-cat-infrastructure",
    podId: "demo-pod-inventory",
    workstreamId: "workstream-3",
    priority: 11,
    capacity: { Engineering: 18 },
    storyPointOverrides: { Engineering: 18 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
  {
    id: "demo-epic-36",
    title: "Auction & Wholesale Integration",
    description:
      "Integrate with wholesale auction platforms (Manheim, ADESA) to ingest off-lease and trade-in vehicles directly into the catalog pipeline with automated condition and pricing data.",
    categoryId: "demo-cat-integration",
    podId: "demo-pod-checkout",
    workstreamId: "workstream-2",
    priority: 7,
    capacity: { Engineering: 20 },
    storyPointOverrides: { Engineering: 20 },
    parentState: 'Open',
    status: 'Needs Refinement',
  },
];

export const scenario1Stories: Story[] = [
  {
      id: "e1-e1",
      epicId: "demo-epic-1",
      sprintId: "sprint-1",
      title: "Vehicle listing API endpoint",
      description:
        "Build paginated REST endpoint returning vehicle summaries with filtering and sorting parameters.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'Closed',
      status: 'Completed',
      acceptanceCriteria:
        '<ol><li><p><strong>Pagination</strong>: endpoint accepts <code>page</code> and <code>pageSize</code> query parameters; response includes <code>total</code>, <code>page</code>, and <code>pages</code> fields.</p></li><li><p><strong>Filtering</strong>: supports filtering by <em>make</em>, <em>model</em>, <em>year range</em>, <em>price range</em>, and <em>mileage range</em>; invalid filter values return a <strong>400 Bad Request</strong>.</p></li><li><p><strong>Sorting</strong>: supports <code>sortBy</code> values of <code>price</code>, <code>mileage</code>, and <code>year</code> with <code>sortDir</code> of <code>asc</code> or <code>desc</code>.</p></li><li><p><strong>Performance</strong>: p95 response time is under <strong>150 ms</strong> for up to 10,000 vehicles in the catalog.</p></li><li><p>API contract is documented in <a href="https://swagger.io/specification/">OpenAPI 3.0</a> format and kept in sync with the implementation.</p></li></ol>',
      history: [
        makeHistory(
          "h-e1e1-2",
          DEMO_AUTHORS.devonWalsh.name,
          "2026-03-12T10:15:00.000Z",
          [{ field: "Story Points", from: "5", to: "8" }],
        ),
        makeHistory(
          "h-e1e1-1",
          DEMO_AUTHORS.rachelTorres.name,
          "2026-03-10T09:00:00.000Z",
          [
            { field: "Title", from: "Listing API", to: "Vehicle listing API endpoint" },
            { field: "Engineering Type", from: "Fullstack", to: "Backend" },
          ],
        ),
      ],
      comments: [
        makeComment(
          "c-e1e1-1",
          DEMO_AUTHORS.devonWalsh,
          "<p>I've scoped this at <strong>8 points</strong> because we need to handle multi-column index creation and query-plan analysis before we can guarantee the 150 ms p95 target. Happy to pair on the DB schema design early next sprint.</p>",
          "2026-03-12T10:20:00.000Z",
          {
            reactions: [
              { emoji: "👍", userIds: [DEMO_AUTHORS.rachelTorres.id, DEMO_AUTHORS.anikaPatel.id] },
              { emoji: "🔥", userIds: [DEMO_AUTHORS.jordanKim.id] },
            ],
            replies: [
              {
                id: "r-e1e1-1-1",
                authorId: DEMO_AUTHORS.rachelTorres.id,
                authorName: DEMO_AUTHORS.rachelTorres.name,
                content: "Agreed — let's schedule that early on Monday. I'll add a calendar invite.",
                createdAt: "2026-03-12T11:05:00.000Z",
              },
              {
                id: "r-e1e1-1-2",
                authorId: DEMO_AUTHORS.currentUser.id,
                authorName: DEMO_AUTHORS.currentUser.name,
                content: "I can help draft the OpenAPI spec in parallel so we're not blocked.",
                createdAt: "2026-03-12T13:30:00.000Z",
              },
            ],
          },
        ),
        makeComment(
          "c-e1e1-2",
          DEMO_AUTHORS.anikaPatel,
          "<p>Quick note: the frontend team is expecting the response to include a <code>thumbnailUrl</code> field on each vehicle summary. Can we confirm that's in scope here or should it be a separate story?</p>",
          "2026-03-13T08:45:00.000Z",
          {
            reactions: [{ emoji: "👍", userIds: [DEMO_AUTHORS.devonWalsh.id] }],
            replies: [
              {
                id: "r-e1e1-2-1",
                authorId: DEMO_AUTHORS.devonWalsh.id,
                authorName: DEMO_AUTHORS.devonWalsh.name,
                content: "Yes, thumbnailUrl is included in the canonical VehicleSummary schema. I'll add it to the OpenAPI spec today.",
                createdAt: "2026-03-13T09:10:00.000Z",
              },
            ],
          },
        ),
      ],
    },
  {
      id: "e1-e2",
      epicId: "demo-epic-1",
      sprintId: "sprint-1",
      title: "Vehicle summary card component",
      description:
        "Implement reusable card component with lazy-loading image, spec badges, and price display.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'Closed',
      status: 'Completed',
      acceptanceCriteria:
        '<ol><li><p>Card displays the vehicle <strong>thumbnail image</strong> with lazy loading; a low-resolution <em>placeholder</em> is shown while the full image loads.</p></li><li><p>Spec badges surface the <strong>top four</strong> attributes: <em>year</em>, <em>mileage</em>, <em>fuel type</em>, and <em>transmission</em>.</p></li><li><p>Price is formatted as a locale-aware currency string (e.g. <strong>$24,999</strong>).</p></li><li><p>Card is fully keyboard-navigable and meets <a href="https://www.w3.org/TR/WCAG21/">WCAG 2.1 AA</a> contrast requirements.</p></li><li><p>Component is covered by <em>unit tests</em> for all prop variations using React Testing Library.</p></li></ol>',
      history: [
        makeHistory(
          "h-e1e2-1",
          DEMO_AUTHORS.marcusLee.name,
          "2026-03-09T14:00:00.000Z",
          [{ field: "Description", from: "(empty)", to: "Implement reusable card component with lazy-loading image, spec badges, and price display." }],
        ),
      ],
      comments: [
        makeComment(
          "c-e1e2-1",
          DEMO_AUTHORS.marcusLee,
          "<p>Sharing the <strong>Figma link</strong> for the card designs: all three states (loading, loaded, error) are covered. Note that on mobile the spec badges collapse to a horizontal scroll strip — please make sure that's reflected in the implementation.</p>",
          "2026-03-09T15:30:00.000Z",
          {
            reactions: [
              { emoji: "👍", userIds: [DEMO_AUTHORS.anikaPatel.id, DEMO_AUTHORS.jordanKim.id] },
              { emoji: "🎉", userIds: [DEMO_AUTHORS.rachelTorres.id] },
            ],
          },
        ),
        makeComment(
          "c-e1e2-2",
          DEMO_AUTHORS.jordanKim,
          "<p>One question on the lazy-loading: are we using native <code>loading=\"lazy\"</code> or a custom IntersectionObserver? The Safari support for native lazy-load improved in v15.4 so I think we're safe to use native now.</p>",
          "2026-03-10T09:20:00.000Z",
          {
            replies: [
              {
                id: "r-e1e2-2-1",
                authorId: DEMO_AUTHORS.anikaPatel.id,
                authorName: DEMO_AUTHORS.anikaPatel.name,
                content: "Let's go native — simpler and no extra bundle weight. I'll add a polyfill note to the acceptance criteria just in case.",
                createdAt: "2026-03-10T10:00:00.000Z",
              },
            ],
          },
        ),
      ],
    },
  {
      id: "e1-e3",
      epicId: "demo-epic-1",
      sprintId: "sprint-1",
      title: "Grid/list view toggle",
      description:
        "Implement toggle control that switches between grid and list layout with persisted user preference.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
    },
  {
      id: "e1-e4",
      epicId: "demo-epic-1",
      sprintId: "sprint-1",
      title: "Pagination component",
      description:
        "Build reusable pagination component with URL-synced page state and configurable page size.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
    },
  {
      id: "e1-e5",
      epicId: "demo-epic-1",
      sprintId: "sprint-1",
      title: "Accessibility enhancements",
      description:
        "Add ARIA labels, keyboard navigation, and screen-reader text to listing page components.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'Closed',
      status: 'Completed',
    },
  {
      id: "e2-e1",
      epicId: "demo-epic-2",
      sprintId: "sprint-2",
      title: "Faceted search API",
      description:
        "Extend listing endpoint to support multi-dimensional filtering with aggregation counts per facet.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'Closed',
      status: 'Completed',
      acceptanceCriteria:
        '<ol><li><p>Each facet response includes the facet <strong>key</strong>, a human-readable <strong>label</strong>, and an array of <em>buckets</em> each with a <code>value</code> and <code>count</code>.</p></li><li><p>Applying multiple facets within the <em>same</em> dimension uses <strong>OR</strong> logic; applying facets across <em>different</em> dimensions uses <strong>AND</strong> logic.</p></li><li><p>Facet counts are <em>always</em> calculated relative to the currently applied cross-dimension filters so counts stay accurate as the user narrows their search.</p></li><li><p>Endpoint is <strong>backwards-compatible</strong> with the existing listing API — consumers that omit facet parameters receive unchanged responses.</p></li><li><p>Refer to the <a href="https://www.elastic.co/guide/en/elasticsearch/reference/current/search-aggregations.html">Elasticsearch aggregations guide</a> for the recommended implementation approach.</p></li></ol>',
    },
  {
      id: "e2-e2",
      epicId: "demo-epic-2",
      sprintId: "sprint-2",
      title: "Filter panel component",
      description:
        "Build filter sidebar with range sliders, multi-select checkboxes, and real-time result count updates.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'Closed',
      status: 'Completed',
    },
  {
      id: "e2-e3",
      epicId: "demo-epic-2",
      sprintId: "sprint-2",
      title: "URL state persistence",
      description:
        "Serialize filter state to URL query params so searches are bookmarkable and shareable.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
    },
  {
      id: "e2-e4",
      epicId: "demo-epic-2",
      sprintId: "sprint-2",
      title: "Applied filters chip bar",
      description:
        'Show applied filters as removable chips above results with a "Clear all" action.',
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
    },
  {
      id: "e3-e1",
      epicId: "demo-epic-3",
      sprintId: "sprint-2",
      title: "Photo gallery carousel",
      description:
        "Implement swipeable image carousel with keyboard support, lazy loading, and fullscreen lightbox.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'Closed',
      status: 'Completed',
      acceptanceCriteria:
        '<ol><li><p>Carousel supports <strong>swipe gestures</strong> on touch devices and <em>left/right arrow keys</em> on desktop.</p></li><li><p>Images are <strong>lazy-loaded</strong>: only the current slide and its immediate neighbours are loaded; remaining images load on demand.</p></li><li><p>Clicking any image opens a <strong>fullscreen lightbox</strong> with a close button and keyboard <code>Escape</code> support.</p></li><li><p>A <em>thumbnail strip</em> below the main image allows direct navigation to any slide.</p></li><li><p>Implements <a href="https://www.w3.org/WAI/ARIA/apg/patterns/carousel/">ARIA carousel pattern</a> with <code>role="region"</code>, <code>aria-label</code>, and live region announcements for slide changes.</p></li></ol>',
    },
  {
      id: "e3-e2",
      epicId: "demo-epic-3",
      sprintId: "sprint-4",
      title: "360° spin viewer",
      description:
        "Integrate 360° spin viewer SDK and build the drag/touch interaction layer.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'In Progress',
      status: 'Blocked',
    },
  {
      id: "e3-e3",
      epicId: "demo-epic-3",
      sprintId: "sprint-3",
      title: "Vehicle spec table component",
      description:
        "Build expandable specification table from structured vehicle data fields.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e3e3-2",
          DEMO_AUTHORS.jordanKim.name,
          "2026-04-10T16:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e3e3-1",
          DEMO_AUTHORS.jordanKim.name,
          "2026-04-01T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e3-e4",
      epicId: "demo-epic-3",
      sprintId: "sprint-4",
      title: "Vehicle history report integration",
      description:
        "Call history report API and render the report summary with a deep-link to full report.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e3-e5",
      epicId: "demo-epic-3",
      sprintId: "sprint-4",
      title: "Sticky CTA panel",
      description:
        "Build sticky sidebar CTA that converts to a bottom bar on mobile with smooth scroll anchor.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 4,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e4-e1",
      epicId: "demo-epic-4",
      title: "Saved vehicles API",
      description:
        "Build authenticated endpoints for adding, removing, and listing saved vehicles with guest session support.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e4-e2",
      epicId: "demo-epic-4",
      title: "Save/unsave UI interaction",
      description:
        "Implement optimistic save toggle on listing and detail pages with undo toast notification.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e4-e3",
      epicId: "demo-epic-4",
      title: "Cart & comparison page",
      description:
        "Build cart page with vehicle cards, price comparison grid, and clear/remove actions.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 6,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e4-e4",
      epicId: "demo-epic-4",
      title: "Guest session persistence",
      description:
        "Store saved vehicle IDs in local storage and migrate to account on sign-in.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e5-e1",
      epicId: "demo-epic-5",
      sprintId: "sprint-3",
      title: "Checkout session API",
      description:
        "Build checkout session service to hold order state across steps with expiry and resumption.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e5e1-2",
          DEMO_AUTHORS.isabelleGrant.name,
          "2026-04-09T17:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e5e1-1",
          DEMO_AUTHORS.isabelleGrant.name,
          "2026-03-30T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e5-e2",
      epicId: "demo-epic-5",
      title: "Payment gateway integration",
      description:
        "Integrate Stripe for card payments, ACH transfers, and webhook-based payment status updates.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'In Progress',
      status: 'Blocked',
      acceptanceCriteria:
        '<ol><li><p><strong>Card payments</strong>: Stripe <a href="https://stripe.com/docs/payments/payment-intents">Payment Intents API</a> is used; <em>3D Secure</em> authentication is handled transparently.</p></li><li><p><strong>ACH transfers</strong>: bank account verification via <a href="https://stripe.com/docs/financial-connections">Stripe Financial Connections</a> is completed before the debit is initiated.</p></li><li><p><strong>Webhooks</strong>: the service handles <code>payment_intent.succeeded</code>, <code>payment_intent.payment_failed</code>, and <code>charge.refunded</code> events; each is processed <em>idempotently</em> using the Stripe event ID.</p></li><li><p>All sensitive card data flows <strong>only</strong> through Stripe-hosted elements; our servers never receive raw PAN data (maintains <strong>PCI DSS SAQ A</strong> scope).</p></li><li><p>Integration is covered by <em>unit tests</em> using Stripe\'s test-mode keys and <em>contract tests</em> against the Stripe sandbox.</p></li></ol>',
      history: [
        makeHistory(
          "h-e5e2-2",
          DEMO_AUTHORS.isabelleGrant.name,
          "2026-03-13T11:30:00.000Z",
          [{ field: "Story Points", from: "8", to: "10" }],
        ),
        makeHistory(
          "h-e5e2-1",
          DEMO_AUTHORS.ethanRivera.name,
          "2026-03-08T10:00:00.000Z",
          [{ field: "Engineering Type", from: "Fullstack", to: "Backend" }],
        ),
      ],
      comments: [
        makeComment(
          "c-e5e2-1",
          DEMO_AUTHORS.isabelleGrant,
          "<p>Important security note for reviewers: Stripe recommends using <strong>Payment Elements</strong> over the legacy Charges API for new integrations. This automatically handles 3DS2 and SCA compliance. I've updated the implementation plan accordingly — bumping points to <strong>10</strong> to account for the additional testing surface.</p>",
          "2026-03-13T11:00:00.000Z",
          {
            reactions: [
              { emoji: "👍", userIds: [DEMO_AUTHORS.ethanRivera.id, DEMO_AUTHORS.zoeYamamoto.id] },
              { emoji: "🔥", userIds: [DEMO_AUTHORS.currentUser.id] },
            ],
            replies: [
              {
                id: "r-e5e2-1-1",
                authorId: DEMO_AUTHORS.ethanRivera.id,
                authorName: DEMO_AUTHORS.ethanRivera.name,
                content: "Fully support this — the 10-point estimate is approved. Let's make sure the PCI DSS evidence is documented for the compliance review.",
                createdAt: "2026-03-13T12:00:00.000Z",
              },
            ],
          },
        ),
        makeComment(
          "c-e5e2-2",
          DEMO_AUTHORS.zoeYamamoto,
          "<p>I'll handle the webhook signature verification and idempotency layer. Will use the Stripe <code>stripe-signature</code> header validation pattern. Setting up a local webhook relay via <code>stripe listen</code> in the dev environment.</p>",
          "2026-03-14T09:00:00.000Z",
          {
            reactions: [{ emoji: "👍", userIds: [DEMO_AUTHORS.isabelleGrant.id] }],
          },
        ),
      ],
    },
  {
      id: "e5-e3",
      epicId: "demo-epic-5",
      title: "Third-party finance integration",
      description:
        "Integrate finance provider API for real-time pre-qualification and loan origination handoff.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e5-e4",
      epicId: "demo-epic-5",
      title: "Multi-step checkout UI",
      description:
        "Build step-by-step checkout form with validation, auto-save, and back-navigation support.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'In Progress',
      status: 'In Progress',
    },
  {
      id: "e5-e5",
      epicId: "demo-epic-5",
      title: "E-signature modal",
      description:
        "Implement DocuSign embedded signing experience within the order confirmation flow.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 4,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e6-e1",
      epicId: "demo-epic-6",
      title: "Lender API integration",
      description:
        "Build server-side integration with multiple lender pre-qualification APIs with response normalisation.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'In Progress',
      status: 'In Progress',
    },
  {
      id: "e6-e2",
      epicId: "demo-epic-6",
      title: "Financing calculator component",
      description:
        "Build interactive calculator with sliders, real-time payment formula, and animated results.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e6-e3",
      epicId: "demo-epic-6",
      title: "Pre-qualification results panel",
      description:
        "Display lender offers with monthly payment, APR, and CTA to proceed with chosen lender.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 4,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e7-e1",
      epicId: "demo-epic-7",
      title: "Auth service (email + OAuth)",
      description:
        "Implement registration, login, password reset, and Google/Apple OAuth using Auth0.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e7-e2",
      epicId: "demo-epic-7",
      title: "Account & profile API",
      description: "Build profile CRUD endpoints and saved searches storage.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e7-e3",
      epicId: "demo-epic-7",
      title: "Account dashboard UI",
      description:
        "Build the profile page, order history list, saved searches panel, and delivery milestone timeline.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e7-e4",
      epicId: "demo-epic-7",
      title: "Auth UI flow",
      description:
        "Build sign-up, sign-in, and OAuth button flows with error handling and redirect logic.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 2,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e8-e1",
      epicId: "demo-epic-8",
      sprintId: "sprint-1b",
      title: "Dealer auth & RBAC",
      description:
        "Build dealer authentication, dealer account model, and role-based access control layer.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 6,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e8e1-2",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-13T16:30:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e8e1-1",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-02T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e8-e2",
      epicId: "demo-epic-8",
      title: "CSV bulk import pipeline",
      description:
        "Build upload endpoint, CSV parsing, validation, error reporting, and async processing queue.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'In Progress',
      status: 'In Progress',
      acceptanceCriteria:
        '<ol><li><p><strong>Upload</strong>: endpoint accepts <code>multipart/form-data</code> with a single CSV file up to <strong>50 MB</strong>; files exceeding the limit receive a <strong>413 Payload Too Large</strong> response.</p></li><li><p><strong>Validation</strong>: each row is validated against the <em>canonical vehicle schema</em>; rows with missing <strong>required fields</strong> (VIN, make, model, year, price) are rejected with a descriptive per-row error message.</p></li><li><p><strong>Error reporting</strong>: after processing, the API returns a summary with <code>successCount</code>, <code>errorCount</code>, and an array of <em>row-level errors</em> each containing the row number and reason.</p></li><li><p><strong>Async processing</strong>: large files (over 500 rows) are queued and processed asynchronously; the dealer receives an <em>email notification</em> when the import completes.</p></li><li><p>See the <a href="https://datatracker.ietf.org/doc/html/rfc4180">RFC 4180 CSV specification</a> for parsing rules; use a battle-tested library rather than a hand-rolled parser.</p></li></ol>',
      history: [
        makeHistory(
          "h-e8e2-1",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-11T13:00:00.000Z",
          [{ field: "Description", from: "Build upload endpoint and CSV parsing.", to: "Build upload endpoint, CSV parsing, validation, error reporting, and async processing queue." }],
        ),
      ],
      comments: [
        makeComment(
          "c-e8e2-1",
          DEMO_AUTHORS.alexChen,
          "<p>Recommending <strong>fast-csv</strong> for parsing — it handles streaming large files natively so we avoid loading a 50 MB file into memory. The async processing queue will use <strong>Bull</strong> (Redis-backed) which is already in our stack.</p>",
          "2026-03-11T13:30:00.000Z",
          {
            reactions: [
              { emoji: "👍", userIds: [DEMO_AUTHORS.dianaFlores.id] },
              { emoji: "🔥", userIds: [DEMO_AUTHORS.currentUser.id] },
            ],
            replies: [
              {
                id: "r-e8e2-1-1",
                authorId: DEMO_AUTHORS.dianaFlores.id,
                authorName: DEMO_AUTHORS.dianaFlores.name,
                content: "Sounds good. Can we also expose a progress endpoint so dealers can poll the import status from the UI?",
                createdAt: "2026-03-11T14:00:00.000Z",
              },
              {
                id: "r-e8e2-1-2",
                authorId: DEMO_AUTHORS.alexChen.id,
                authorName: DEMO_AUTHORS.alexChen.name,
                content: "Yes — Bull exposes job progress natively. I'll add a GET /imports/:jobId/status endpoint that proxies the queue status. Adding to the acceptance criteria.",
                createdAt: "2026-03-11T14:20:00.000Z",
              },
            ],
          },
        ),
        makeComment(
          "c-e8e2-2",
          DEMO_AUTHORS.currentUser,
          "<p>One edge case to handle: what happens if a dealer uploads a file with duplicate VINs within the <em>same</em> batch? Should the second row overwrite the first, or should both be flagged as errors?</p>",
          "2026-03-12T08:50:00.000Z",
          {
            reactions: [{ emoji: "👍", userIds: [DEMO_AUTHORS.alexChen.id, DEMO_AUTHORS.dianaFlores.id] }],
            replies: [
              {
                id: "r-e8e2-2-1",
                authorId: DEMO_AUTHORS.dianaFlores.id,
                authorName: DEMO_AUTHORS.dianaFlores.name,
                content: "Flag both as errors and include the row numbers — dealers should fix their feed rather than us silently dropping data.",
                createdAt: "2026-03-12T09:15:00.000Z",
              },
            ],
          },
        ),
      ],
    },
  {
      id: "e8-e3",
      epicId: "demo-epic-8",
      title: "Real-time inventory sync webhook",
      description:
        "Implement outbound webhook that pushes inventory changes to the storefront search index.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 6,
      parentState: 'In Progress',
      status: 'In Progress',
    },
  {
      id: "e8-e4",
      epicId: "demo-epic-8",
      title: "Dealer portal UI",
      description:
        "Build portal shell with listing management table, status badges, and bulk action controls.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e8-e5",
      epicId: "demo-epic-8",
      title: "Analytics dashboard UI",
      description:
        "Build charts and tables for dealer performance analytics using Recharts.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e9-e1",
      epicId: "demo-epic-9",
      sprintId: "sprint-1c",
      title: "Feed normalisation layer",
      description:
        "Build ingestion transformer that maps disparate dealer CSV/JSON formats to the canonical vehicle schema.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e9e1-2",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-13T15:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e9e1-1",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-02T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e9-e2",
      epicId: "demo-epic-9",
      sprintId: "sprint-1c",
      title: "NHTSA & VIN decode integration",
      description:
        "Integrate NHTSA API and VIN decode service to fill missing specification fields.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e9e2-2",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-13T16:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e9e2-1",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-04T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e9-e3",
      epicId: "demo-epic-9",
      sprintId: "sprint-2c",
      title: "SEO description generator",
      description:
        "Build template-based description generator that produces keyword-rich text from structured vehicle data.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e9e3-2",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-25T16:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e9e3-1",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-16T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e9-e4",
      epicId: "demo-epic-9",
      sprintId: "sprint-2c",
      title: "Data quality flagging & alerting",
      description:
        "Implement quality scoring rules and send Slack/email alerts when thresholds are breached.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e9e4-2",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-26T14:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e9e4-1",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-18T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e9-e5",
      epicId: "demo-epic-9",
      sprintId: "sprint-2c",
      title: "Data quality dashboard UI",
      description:
        "Build admin dashboard showing pipeline run history, error logs, and quality score charts.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 2,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e9e5-2",
          DEMO_AUTHORS.dianaFlores.name,
          "2026-03-27T15:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e9e5-1",
          DEMO_AUTHORS.dianaFlores.name,
          "2026-03-23T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e10-e1",
      epicId: "demo-epic-10",
      sprintId: "sprint-1c",
      title: "Elasticsearch cluster provisioning",
      description:
        "Provision and configure Elasticsearch cluster on AWS with ILM policies and snapshot backups.",
      type: "Engineering",
      engineeringSubtype: "DevOps",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e10e1-2",
          DEMO_AUTHORS.dianaFlores.name,
          "2026-03-12T17:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e10e1-1",
          DEMO_AUTHORS.dianaFlores.name,
          "2026-03-05T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e10-e2",
      epicId: "demo-epic-10",
      sprintId: "sprint-2c",
      title: "Vehicle index schema & mappings",
      description:
        "Design Elasticsearch index mappings with appropriate analysers for text fields and numeric ranges.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'Closed',
      status: 'Completed',
      history: [
        makeHistory(
          "h-e10e2-2",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-20T16:00:00.000Z",
          [{ field: "Status", from: "In Progress", to: "Completed" }, { field: "Parent State", from: "In Progress", to: "Closed" }],
        ),
        makeHistory(
          "h-e10e2-1",
          DEMO_AUTHORS.alexChen.name,
          "2026-03-16T09:00:00.000Z",
          [{ field: "Status", from: "Not Started", to: "In Progress" }],
        ),
      ],
    },
  {
      id: "e10-e3",
      epicId: "demo-epic-10",
      title: "Bulk indexing pipeline",
      description:
        "Build initial bulk-load pipeline to populate the index from the catalog database.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'In Progress',
      status: 'In Progress',
    },
  {
      id: "e10-e4",
      epicId: "demo-epic-10",
      title: "Real-time index update consumer",
      description:
        "Build Kafka consumer that listens to inventory change events and applies incremental index updates.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e10-e5",
      epicId: "demo-epic-10",
      title: "Search query optimisation",
      description:
        "Tune Elasticsearch queries for sub-100ms p95 latency with caching, field boosting, and explain analysis.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 4,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e11-e1",
      epicId: "demo-epic-11",
      title: "Behavioural event tracking",
      description:
        "Instrument browse history, search, and save events and pipe to the ML feature store.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e11-e2",
      epicId: "demo-epic-11",
      title: "Recommendation model training pipeline",
      description:
        "Build collaborative-filtering model training pipeline on collected behavioural data.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e11-e3",
      epicId: "demo-epic-11",
      title: "Recommendation serving API",
      description:
        "Build low-latency recommendation API backed by the trained model with real-time candidate ranking.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e11-e4",
      epicId: "demo-epic-11",
      title: "Recommendation carousel UI",
      description:
        "Build personalised carousel component with A/B test variant support and impression tracking.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 6,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e12-e1",
      epicId: "demo-epic-12",
      title: "CMS integration for banners",
      description:
        "Integrate headless CMS to serve dynamic hero and promotional banner content.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e12-e2",
      epicId: "demo-epic-12",
      title: "Hero banner component",
      description:
        "Build animated hero component with responsive layouts and CMS-driven content.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e12-e3",
      epicId: "demo-epic-12",
      title: "Campaign impression tracking",
      description:
        "Instrument banner impression and click events to the analytics pipeline.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 7,
      parentState: 'In Progress',
      status: 'Not Started',
    },
  {
      id: "e14-e1",
      epicId: "demo-epic-14",
      title: "Comparison state management",
      description:
        "Build Redux state for the comparison tray with add/remove and max-vehicle enforcement.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e14-e2",
      epicId: "demo-epic-14",
      title: "Comparison table component",
      description:
        "Build side-by-side spec comparison table with difference highlighting.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 8,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e14-e3",
      epicId: "demo-epic-14",
      title: "Comparison tray component",
      description:
        "Build floating tray showing queued vehicles with thumbnail, price, and compare CTA.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 9,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e16-e1",
      epicId: "demo-epic-16",
      title: "Reviews API",
      description:
        "Build endpoints for submitting, moderating, and fetching paginated reviews with aggregate stats.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e16-e2",
      epicId: "demo-epic-16",
      title: "Review submission UI",
      description:
        "Build review form with star rating, text input, and photo upload capability.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e16-e3",
      epicId: "demo-epic-16",
      title: "Review display UI",
      description:
        "Build review list component with rating summary bar and individual review cards.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e21-e1",
      epicId: "demo-epic-21",
      title: "Tax calculation service",
      description:
        "Build server-side tax and fee calculation service backed by jurisdiction rule tables.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e21-e2",
      epicId: "demo-epic-21",
      title: "Fee breakdown UI component",
      description:
        "Build expandable fee breakdown in the checkout order summary with real-time recalculation.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 6,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e21-e3",
      epicId: "demo-epic-21",
      title: "Jurisdiction rule management",
      description:
        "Build internal admin tool for updating state tax rates and fee rules without code deploys.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 4,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e22-e1",
      epicId: "demo-epic-22",
      title: "Appraisal API integration",
      description:
        "Integrate trade-in appraisal provider API and cache valuation results against VIN.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 10,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e22-e2",
      epicId: "demo-epic-22",
      title: "Trade-in form UI",
      description:
        "Build multi-step trade-in data entry form with VIN lookup and condition questionnaire.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 7,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e22-e3",
      epicId: "demo-epic-22",
      title: "Trade-in credit checkout integration",
      description:
        "Apply accepted trade-in value as a line-item credit in the checkout order summary.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e29-e1",
      epicId: "demo-epic-29",
      title: "Image ingestion & validation pipeline",
      description:
        "Build async pipeline to receive, validate dimensions/quality, and store vehicle images to S3.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e29-e2",
      epicId: "demo-epic-29",
      title: "Responsive image variant generation",
      description:
        "Automatically generate thumbnail, medium, and full-size WebP variants via an image processing queue.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 6,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e29-e3",
      epicId: "demo-epic-29",
      title: "CDN asset delivery integration",
      description:
        "Serve all image variants through CloudFront with cache invalidation on update.",
      type: "Engineering",
      engineeringSubtype: "DevOps",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e29-e4",
      epicId: "demo-epic-29",
      title: "Media management UI",
      description:
        "Build dealer-facing interface for uploading, reordering, and deleting vehicle photos with drag-and-drop support.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Open',
      status: 'Ready',
    },
  {
      id: "e30-e1",
      epicId: "demo-epic-30",
      title: "Market data ingestion",
      description:
        "Build nightly ingestion pipeline to pull comparable vehicle pricing from the market data provider.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'Open',
      status: 'Needs Refinement',
    },
  {
      id: "e30-e2",
      epicId: "demo-epic-30",
      title: "Price competitiveness scoring",
      description:
        "Calculate per-listing competitiveness score by comparing dealer price to market median for matching vehicles.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 8,
      parentState: 'Open',
      status: 'Needs Refinement',
    },
  {
      id: "e30-e3",
      epicId: "demo-epic-30",
      title: "Pricing recommendations API",
      description:
        "Expose pricing recommendations via an internal API consumed by the dealer portal and storefront.",
      type: "Engineering",
      engineeringSubtype: "Backend",
      points: 5,
      parentState: 'Open',
      status: 'Needs Refinement',
    },
  {
      id: "e30-e4",
      epicId: "demo-epic-30",
      title: "Price intelligence dashboard UI",
      description:
        "Build dealer dashboard visualising market price bands and pricing recommendations.",
      type: "Engineering",
      engineeringSubtype: "Frontend",
      points: 5,
      parentState: 'Open',
      status: 'Needs Refinement',
    },
];

export const scenario1SprintForecastConfig = {
  startDate: (() => {
    const d = new Date("2026-03-02"); // Monday (first Monday of March 2026)
    return d.toISOString();
  })(),
  sprintLength: 10,
  totalSprints: 10,
};

export const scenario1HealthConfig: {
  epicWeights: Record<string, number>;
  storyWeights: Record<string, number>;
} = {
  epicWeights: {
    no_description: 20,
    no_stories: 15,
    score_overridden: 5,
    no_workstream: 15,
  },
  storyWeights: {
    no_description: 20,
    no_points: 15,
    no_acceptance_criteria: 15,
  },
};

export const scenario1SprintStateConfig: {
  epicStatuses: Record<string, Array<{ id: string; title: string; color: string }>>;
  storyStatuses: Record<string, Array<{ id: string; title: string; color: string }>>;
} = {
  epicStatuses: {
    Open: [
      { id: "epic-ready", title: "Ready", color: "#2196F3" },
      { id: "epic-needs-refinement", title: "Needs Refinement", color: "#FF9800" },
    ],
    "In Progress": [
      { id: "epic-not-started", title: "Not Started", color: "#9E9E9E" },
      { id: "epic-blocked", title: "Blocked", color: "#FF5722" },
      { id: "epic-in-progress", title: "In Progress", color: "#2196F3" },
      { id: "epic-completed", title: "Completed", color: "#4CAF50" },
    ],
    Closed: [],
  },
  storyStatuses: {
    Open: [
      { id: "story-ready", title: "Ready", color: "#2196F3" },
      { id: "story-needs-refinement", title: "Needs Refinement", color: "#FF9800" },
    ],
    "In Progress": [
      { id: "story-not-started", title: "Not Started", color: "#9E9E9E" },
      { id: "story-blocked", title: "Blocked", color: "#FF5722" },
      { id: "story-in-progress", title: "In Progress", color: "#2196F3" },
      { id: "story-completed", title: "Completed", color: "#4CAF50" },
    ],
    Closed: [],
  },
};

// ---------------------------------------------------------------------------
// Sprints
// ---------------------------------------------------------------------------

/**
 * Actual team sprints used to organise stories and drive workflow state
 * transitions.  Distinct from scenario1SprintForecastConfig which powers
 * the capacity-planning forecaster.
 *
 * Sprint IDs follow the pattern "sprint-<n>" for the Storefront & Checkout
 * board and "sprint-<n>b" for the parallel Inventory & Catalog board, making
 * it clear that two teams can run simultaneous "Sprint 1" iterations as
 * separate records with globally unique IDs.
 *
 * Sprint IDs are referenced as sprintId on individual Story records.
 */
export const scenario1Sprints: Sprint[] = [
  // ── Storefront & Checkout track ─────────────────────────────────────────
  {
    id: "sprint-1",
    name: "Sprint 1",
    goal: "Establish core vehicle listing infrastructure: deliver the paginated listing API, vehicle summary card, search bar, and listing page shell so the Storefront pod has a working end-to-end listing flow.",
    startDate: "2026-03-02T00:00:00.000Z",
    endDate: "2026-03-13T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 34,
    completedCapacity: 34,
    notes: "All committed stories were delivered. The Backend and Frontend tracks finished in sync, enabling a joint demo on the last day.",
    retrospective: "Well-planned sprint with clear scope. Going forward the team wants to front-load API contract reviews so FE and BE can work in parallel from day one.",
    createdAt: "2026-02-26T09:00:00.000Z",
    updatedAt: "2026-03-14T10:00:00.000Z",
    history: [
      makeHistory("sh-1-3", DEMO_AUTHORS.rachelTorres.name, "2026-03-14T10:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-1-2", DEMO_AUTHORS.rachelTorres.name, "2026-03-02T08:00:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-1-1", DEMO_AUTHORS.rachelTorres.name, "2026-02-26T09:00:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-2",
    name: "Sprint 2",
    goal: "Complete advanced search and vehicle detail foundations: deliver faceted-search UI, detail-page hero section, and the core payment-processing API so the product is shoppable end-to-end.",
    startDate: "2026-03-16T00:00:00.000Z",
    endDate: "2026-03-27T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 37,
    completedCapacity: 32,
    notes: "Payment API (e2-e4) was descoped mid-sprint due to a third-party PCI compliance dependency — moved to Sprint 3. All other stories completed.",
    retrospective: "Need earlier visibility on external dependencies. The PCI blocker was identified too late in the sprint. Added a dependency-check step to sprint planning.",
    createdAt: "2026-03-10T09:00:00.000Z",
    updatedAt: "2026-03-28T11:00:00.000Z",
    history: [
      makeHistory("sh-2-4", DEMO_AUTHORS.ethanRivera.name, "2026-03-28T11:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-2-3", DEMO_AUTHORS.rachelTorres.name, "2026-03-20T14:00:00.000Z",
        [{ field: "Goal", from: "(original)", to: "Complete advanced search and vehicle detail foundations: deliver faceted-search UI, detail-page hero section, and the core payment-processing API so the product is shoppable end-to-end." }]),
      makeHistory("sh-2-2", DEMO_AUTHORS.rachelTorres.name, "2026-03-16T08:00:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-2-1", DEMO_AUTHORS.rachelTorres.name, "2026-03-10T09:00:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-3",
    name: "Sprint 3",
    goal: "Unblock payment integration and advance vehicle detail page: resolve the PCI compliance dependency, complete checkout flow stories, and progress the vehicle detail spec-sheet component.",
    startDate: "2026-03-30T00:00:00.000Z",
    endDate: "2026-04-10T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 36,
    completedCapacity: 15,
    notes: "Checkout session API (e5-e1) and spec table component (e3-e3) delivered. The 360° spin viewer (e3-e2) remained blocked on Figma design handoff and was carried over to Sprint 4 along with two other detail-page stories.",
    retrospective: "Blocking dependencies need to be flagged in planning, not discovered mid-sprint. Adopted a pre-sprint dependency checklist. Carried-over stories are fully refined and ready for Sprint 4.",
    createdAt: "2026-03-24T09:00:00.000Z",
    updatedAt: "2026-04-11T10:00:00.000Z",
    history: [
      makeHistory("sh-3-4", DEMO_AUTHORS.rachelTorres.name, "2026-04-11T10:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-3-3", DEMO_AUTHORS.rachelTorres.name, "2026-04-06T09:00:00.000Z",
        [{ field: "Notes", from: "(none)", to: "e3-e2 spin-viewer blocked; e3-e4 and e3-e5 moved to Sprint 4." }]),
      makeHistory("sh-3-2", DEMO_AUTHORS.ethanRivera.name, "2026-03-30T08:00:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-3-1", DEMO_AUTHORS.ethanRivera.name, "2026-03-24T09:00:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-4",
    name: "Sprint 4",
    goal: "Complete the vehicle detail page: unblock and deliver the 360° spin viewer, vehicle history report integration, and sticky CTA panel. Begin shopping cart API development.",
    startDate: "2026-04-13T00:00:00.000Z",
    endDate: "2026-04-24T23:59:59.000Z",
    status: "Active" as SprintStatus,
    plannedCapacity: 38,
    notes: "e3-e2 (360° spin viewer) unblocked after Figma handoff received 2026-04-11. All carried-over detail-page stories are in progress. Cart API (e4-e1) added to sprint to start the next epic.",
    createdAt: "2026-03-31T09:00:00.000Z",
    updatedAt: "2026-04-13T08:00:00.000Z",
    history: [
      makeHistory("sh-4-2", DEMO_AUTHORS.rachelTorres.name, "2026-04-13T08:00:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-4-1", DEMO_AUTHORS.rachelTorres.name, "2026-03-31T09:00:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-5",
    name: "Sprint 5",
    goal: "Deliver the complete vehicle detail page and advance shopping cart: wrap up all detail-page stories, ship saved vehicles wishlist API and UI, and guest session persistence.",
    startDate: "2026-04-27T00:00:00.000Z",
    endDate: "2026-05-08T23:59:59.000Z",
    status: "Planning" as SprintStatus,
    plannedCapacity: 36,
    notes: "Story sizing session scheduled for 2026-04-23. Candidate stories from epics 3 and 4 are under refinement.",
    createdAt: "2026-04-11T09:00:00.000Z",
    history: [
      makeHistory("sh-5-1", DEMO_AUTHORS.rachelTorres.name, "2026-04-11T09:00:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },

  // ── Checkout & Payments track (parallel cadence) ─────────────────────────
  // These sprints run on the same calendar dates as the Storefront track but
  // are separate records — each workstream owns its own sprint schedule.
  {
    id: "sprint-1b",
    name: "Sprint 1",
    goal: "Stand up the Checkout & Payments foundations: implement dealer auth and RBAC, establish the checkout session API skeleton, and align on payment gateway integration approach.",
    startDate: "2026-03-02T00:00:00.000Z",
    endDate: "2026-03-13T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 24,
    completedCapacity: 21,
    notes: "Dealer auth & RBAC (e8-e1) delivered. Checkout session API foundations complete. PCI compliance review for payment gateway initiated but not yet unblocked.",
    retrospective: "PCI dependency needs procurement escalation earlier. Added a compliance pre-check to sprint planning gate for payment-related stories.",
    createdAt: "2026-02-26T09:30:00.000Z",
    updatedAt: "2026-03-14T11:00:00.000Z",
    history: [
      makeHistory("sh-1b-3", DEMO_AUTHORS.ethanRivera.name, "2026-03-14T11:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-1b-2", DEMO_AUTHORS.ethanRivera.name, "2026-03-02T08:30:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-1b-1", DEMO_AUTHORS.ethanRivera.name, "2026-02-26T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-2b",
    name: "Sprint 2",
    goal: "Complete checkout session API, advance CSV bulk import pipeline, and begin real-time inventory sync webhook.",
    startDate: "2026-03-16T00:00:00.000Z",
    endDate: "2026-03-27T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 32,
    completedCapacity: 32,
    notes: "Clean sprint — checkout session API and CSV bulk import foundations delivered. Inventory sync webhook in progress.",
    retrospective: "The compliance pre-check gate paid off. No PCI blockers this sprint. Carrying forward the webhook story into Sprint 3 to maintain pace.",
    createdAt: "2026-03-10T09:30:00.000Z",
    updatedAt: "2026-03-28T12:00:00.000Z",
    history: [
      makeHistory("sh-2b-3", DEMO_AUTHORS.ethanRivera.name, "2026-03-28T12:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-2b-2", DEMO_AUTHORS.ethanRivera.name, "2026-03-16T08:30:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-2b-1", DEMO_AUTHORS.ethanRivera.name, "2026-03-10T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-3b",
    name: "Sprint 3",
    goal: "Complete the dealer portal MVP: finish real-time inventory sync webhook, dealer portal UI, and analytics dashboard. Begin financing calculator lender API integration.",
    startDate: "2026-03-30T00:00:00.000Z",
    endDate: "2026-04-10T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 34,
    completedCapacity: 30,
    notes: "Dealer portal UI and analytics dashboard deferred to Sprint 4 — real-time sync webhook complexity higher than estimated. Webhook and lender API integration delivered.",
    retrospective: "Webhook complexity was underestimated. Stories touching external event buses should carry a complexity spike in estimation. Added a 'system-boundary' label to flag these.",
    createdAt: "2026-03-24T09:30:00.000Z",
    updatedAt: "2026-04-11T11:00:00.000Z",
    history: [
      makeHistory("sh-3b-4", DEMO_AUTHORS.ethanRivera.name, "2026-04-11T11:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-3b-3", DEMO_AUTHORS.ethanRivera.name, "2026-04-07T10:00:00.000Z",
        [{ field: "Notes", from: "(none)", to: "Dealer portal UI and analytics dashboard carried to Sprint 4." }]),
      makeHistory("sh-3b-2", DEMO_AUTHORS.ethanRivera.name, "2026-03-30T08:30:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-3b-1", DEMO_AUTHORS.ethanRivera.name, "2026-03-24T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-4b",
    name: "Sprint 4",
    goal: "Complete dealer portal UI and analytics dashboard, finish the financing calculator UI, and advance payment gateway integration toward PCI sign-off.",
    startDate: "2026-04-13T00:00:00.000Z",
    endDate: "2026-04-24T23:59:59.000Z",
    status: "Active" as SprintStatus,
    plannedCapacity: 36,
    notes: "PCI compliance review approved 2026-04-10 — payment gateway story unblocked. Dealer portal UI and analytics dashboard carried from Sprint 3 are actively in development.",
    createdAt: "2026-04-01T09:30:00.000Z",
    updatedAt: "2026-04-13T08:30:00.000Z",
    history: [
      makeHistory("sh-4b-2", DEMO_AUTHORS.ethanRivera.name, "2026-04-13T08:30:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-4b-1", DEMO_AUTHORS.ethanRivera.name, "2026-04-01T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-5b",
    name: "Sprint 5",
    goal: "Wrap up payment gateway integration, deliver pre-qualification results panel, and begin trade-in valuation API integration.",
    startDate: "2026-04-27T00:00:00.000Z",
    endDate: "2026-05-08T23:59:59.000Z",
    status: "Planning" as SprintStatus,
    plannedCapacity: 34,
    notes: "Story sizing session scheduled for 2026-04-23. Payment gateway and financing calculator stories at top of candidate list.",
    createdAt: "2026-04-11T09:30:00.000Z",
    history: [
      makeHistory("sh-5b-1", DEMO_AUTHORS.ethanRivera.name, "2026-04-11T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },

  // ── Inventory & Catalog track (parallel cadence) ─────────────────────────
  // These sprints run on the same calendar dates as the other workstream
  // tracks but are separate records — each workstream owns its own schedule.
  {
    id: "sprint-1c",
    name: "Sprint 1",
    goal: "Stand up the core inventory ingestion pipeline: implement the vehicle data import API, establish the catalog data model, and ship the first iteration of the dealer-facing inventory dashboard.",
    startDate: "2026-03-02T00:00:00.000Z",
    endDate: "2026-03-13T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 32,
    completedCapacity: 28,
    notes: "Feed normalisation layer (e9-e1) and NHTSA/VIN decode integration (e9-e2) delivered. Elasticsearch cluster provisioned. Bulk CSV import slipped to Sprint 2 due to schema review.",
    retrospective: "Schema reviews need to be a planning-gate item, not a mid-sprint discovery. Added schema sign-off to our definition of ready.",
    createdAt: "2026-02-26T09:30:00.000Z",
    updatedAt: "2026-03-14T11:00:00.000Z",
    history: [
      makeHistory("sh-1c-3", DEMO_AUTHORS.dianaFlores.name, "2026-03-14T11:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-1c-2", DEMO_AUTHORS.dianaFlores.name, "2026-03-02T08:30:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-1c-1", DEMO_AUTHORS.dianaFlores.name, "2026-02-26T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-2c",
    name: "Sprint 2",
    goal: "Complete catalog enrichment pipeline: deliver SEO description generator, data quality flagging, data quality dashboard, vehicle index schema, and Elasticsearch cluster setup.",
    startDate: "2026-03-16T00:00:00.000Z",
    endDate: "2026-03-27T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 35,
    completedCapacity: 35,
    notes: "Clean sprint — all stories delivered including the deferred catalog enrichment items from Sprint 1. Elasticsearch index schema and cluster fully live.",
    retrospective: "Great momentum. The schema sign-off gate introduced last sprint paid off immediately. No mid-sprint blockers.",
    createdAt: "2026-03-10T09:30:00.000Z",
    updatedAt: "2026-03-28T12:00:00.000Z",
    history: [
      makeHistory("sh-2c-3", DEMO_AUTHORS.dianaFlores.name, "2026-03-28T12:00:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-2c-2", DEMO_AUTHORS.dianaFlores.name, "2026-03-16T08:30:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-2c-1", DEMO_AUTHORS.dianaFlores.name, "2026-03-10T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-3c",
    name: "Sprint 3",
    goal: "Complete bulk indexing pipeline, begin real-time index update consumer, and start the dealer portal authentication work.",
    startDate: "2026-03-30T00:00:00.000Z",
    endDate: "2026-04-10T23:59:59.000Z",
    status: "Completed" as SprintStatus,
    plannedCapacity: 34,
    completedCapacity: 28,
    notes: "Bulk indexing pipeline partially complete; real-time Kafka consumer kicked off. Dealer portal auth items deferred to Sprint 4.",
    retrospective: "Kafka consumer complexity was underestimated. Stories that cross service boundaries should include a spike story in the same sprint. Added to our estimation checklist.",
    createdAt: "2026-03-24T09:30:00.000Z",
    updatedAt: "2026-04-11T11:30:00.000Z",
    history: [
      makeHistory("sh-3c-4", DEMO_AUTHORS.dianaFlores.name, "2026-04-11T11:30:00.000Z",
        [{ field: "Status", from: "Active", to: "Completed" }]),
      makeHistory("sh-3c-3", DEMO_AUTHORS.dianaFlores.name, "2026-04-07T14:00:00.000Z",
        [{ field: "Notes", from: "(none)", to: "Kafka consumer complexity higher than estimated; real-time update consumer carries to Sprint 4." }]),
      makeHistory("sh-3c-2", DEMO_AUTHORS.dianaFlores.name, "2026-03-30T08:30:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-3c-1", DEMO_AUTHORS.dianaFlores.name, "2026-03-24T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-4c",
    name: "Sprint 4",
    goal: "Complete real-time index update consumer, finish search query optimisation, and advance photo & media asset management pipeline.",
    startDate: "2026-04-13T00:00:00.000Z",
    endDate: "2026-04-24T23:59:59.000Z",
    status: "Active" as SprintStatus,
    plannedCapacity: 34,
    notes: "Real-time index consumer carried from Sprint 3 is top priority. Search optimisation spike underway. Image ingestion pipeline being scoped.",
    createdAt: "2026-04-01T09:30:00.000Z",
    updatedAt: "2026-04-13T09:00:00.000Z",
    history: [
      makeHistory("sh-4c-2", DEMO_AUTHORS.dianaFlores.name, "2026-04-13T09:00:00.000Z",
        [{ field: "Status", from: "Planning", to: "Active" }]),
      makeHistory("sh-4c-1", DEMO_AUTHORS.dianaFlores.name, "2026-04-01T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
  {
    id: "sprint-5c",
    name: "Sprint 5",
    goal: "Complete search query optimisation, deliver image ingestion and responsive variant generation pipeline, and begin CDN asset delivery integration.",
    startDate: "2026-04-27T00:00:00.000Z",
    endDate: "2026-05-08T23:59:59.000Z",
    status: "Planning" as SprintStatus,
    plannedCapacity: 34,
    notes: "Story sizing session scheduled for 2026-04-23. Image pipeline and search performance stories at top of candidate list.",
    createdAt: "2026-04-11T09:30:00.000Z",
    history: [
      makeHistory("sh-5c-1", DEMO_AUTHORS.dianaFlores.name, "2026-04-11T09:30:00.000Z",
        [{ field: "Status", from: "(created)", to: "Planning" }]),
    ] as SprintHistoryEntry[],
  },
];

// ---------------------------------------------------------------------------
// Workstreams
// ---------------------------------------------------------------------------

/**
 * Workstreams define which pods run which sprints together.
 *
 * Hierarchy:
 *   Workstream (the view / team cadence)
 *     └─ WorkstreamEntry[]  (one entry per sprint iteration)
 *           ├─ sprintId  → references a Sprint record
 *           └─ podIds[]  → the pods working that sprint on this workstream
 *
 * Two workstreams can reference overlapping sprint date ranges by pointing to
 * different Sprint records (e.g. "sprint-1" and "sprint-1b" both cover
 * Mar 2–13 but belong to separate workstream tracks).  Sprint IDs are always
 * globally unique even when the sprint name ("Sprint 1") is reused.
 */
export const scenario1Workstreams: Workstream[] = [
  {
    id: "workstream-1",
    name: "Storefront",
    description:
      "Covers the customer-facing browse, search, and purchase experience. The Storefront and Checkout & Payments pods share a sprint cadence on this workstream.",
    entries: [
      { sprintId: "sprint-1", podIds: ["demo-pod-storefront"] } as WorkstreamEntry,
      { sprintId: "sprint-2", podIds: ["demo-pod-storefront"] } as WorkstreamEntry,
      { sprintId: "sprint-3", podIds: ["demo-pod-storefront"] } as WorkstreamEntry,
      { sprintId: "sprint-4", podIds: ["demo-pod-storefront"] } as WorkstreamEntry,
      { sprintId: "sprint-5", podIds: ["demo-pod-storefront"] } as WorkstreamEntry,
    ],
    states: [...DEFAULT_WORKSTREAM_STATES],
    createdAt: "2026-02-26T09:00:00.000Z",
    updatedAt: "2026-04-11T09:00:00.000Z",
  },
  {
    id: "workstream-2",
    name: "Checkout & Payments",
    description:
      "Covers the checkout and payment processing experience. The Checkout pod runs its own sprint cadence on this workstream, parallel to the Storefront & Inventory & Catalog track.",
    entries: [
      { sprintId: "sprint-1b", podIds: ["demo-pod-checkout"] } as WorkstreamEntry,
      { sprintId: "sprint-2b", podIds: ["demo-pod-checkout"] } as WorkstreamEntry,
      { sprintId: "sprint-3b", podIds: ["demo-pod-checkout"] } as WorkstreamEntry,
      { sprintId: "sprint-4b", podIds: ["demo-pod-checkout"] } as WorkstreamEntry,
      { sprintId: "sprint-5b", podIds: ["demo-pod-checkout"] } as WorkstreamEntry,
    ],
    states: [...DEFAULT_WORKSTREAM_STATES],
    createdAt: "2026-02-26T09:30:00.000Z",
    updatedAt: "2026-04-11T09:30:00.000Z",
  },
  {
    id: "workstream-3",
    name: "Inventory & Catalog",
    description:
      "Covers vehicle inventory ingestion, catalog enrichment, and dealer portal integrations. The Inventory & Catalog pod runs its own sprint cadence on this workstream, parallel to the Storefront & Checkout track.",
    entries: [
      { sprintId: "sprint-1c", podIds: ["demo-pod-inventory"] } as WorkstreamEntry,
      { sprintId: "sprint-2c", podIds: ["demo-pod-inventory"] } as WorkstreamEntry,
      { sprintId: "sprint-3c", podIds: ["demo-pod-inventory"] } as WorkstreamEntry,
      { sprintId: "sprint-4c", podIds: ["demo-pod-inventory"] } as WorkstreamEntry,
      { sprintId: "sprint-5c", podIds: ["demo-pod-inventory"] } as WorkstreamEntry,
    ],
    states: [...DEFAULT_WORKSTREAM_STATES],
    createdAt: "2026-02-26T09:30:00.000Z",
    updatedAt: "2026-04-11T09:30:00.000Z",
  },
];

// ---------------------------------------------------------------------------
// WorkstreamSprints
// ---------------------------------------------------------------------------

/**
 * WorkstreamSprint records link individual stories to planning sprint slots
 * within each workstream. Used by the WorkstreamForecaster to track what is
 * committed per sprint. Story IDs reference Story records in scenario1Stories.
 *
 * Sprint numbering is 1-based and sequential within each workstream.
 */
export const scenario1WorkstreamSprints: WorkstreamSprint[] = [
  // ── Workstream 1: Storefront ─────────────────────────────────────────────
  {
    id: "ws1-s1",
    workstreamId: "workstream-1",
    number: 1,
    nickname: "Sprint 1 – Listing Foundation",
    status: "Closed",
    storyIds: ["e1-e1", "e1-e2", "e1-e3", "e1-e4", "e1-e5"],
  },
  {
    id: "ws1-s2",
    workstreamId: "workstream-1",
    number: 2,
    nickname: "Sprint 2 – Search & Detail",
    status: "Closed",
    storyIds: ["e2-e1", "e2-e2", "e2-e3", "e2-e4", "e3-e1"],
  },
  {
    id: "ws1-s3",
    workstreamId: "workstream-1",
    number: 3,
    nickname: "Sprint 3 – Checkout & Spec Table",
    status: "Closed",
    storyIds: ["e3-e3", "e5-e1"],
  },
  {
    id: "ws1-s4",
    workstreamId: "workstream-1",
    number: 4,
    nickname: "Sprint 4 – Detail Page Completion",
    status: "Active",
    storyIds: ["e3-e2", "e3-e4", "e3-e5", "e4-e1"],
  },
  {
    id: "ws1-s5",
    workstreamId: "workstream-1",
    number: 5,
    nickname: "Sprint 5 – Cart & Saved Vehicles",
    status: "Scheduled",
    storyIds: [],
  },

  // ── Workstream 2: Checkout & Payments ────────────────────────────────────
  {
    id: "ws2-s1",
    workstreamId: "workstream-2",
    number: 1,
    nickname: "Sprint 1 – Auth & Session Foundations",
    status: "Closed",
    storyIds: ["e8-e1"],
  },
  {
    id: "ws2-s2",
    workstreamId: "workstream-2",
    number: 2,
    nickname: "Sprint 2 – Checkout Session & Import",
    status: "Closed",
    storyIds: ["e8-e2", "e8-e3"],
  },
  {
    id: "ws2-s3",
    workstreamId: "workstream-2",
    number: 3,
    nickname: "Sprint 3 – Portal MVP & Lender API",
    status: "Closed",
    storyIds: ["e6-e1"],
  },
  {
    id: "ws2-s4",
    workstreamId: "workstream-2",
    number: 4,
    nickname: "Sprint 4 – Portal UI & Payment Gateway",
    status: "Active",
    storyIds: ["e5-e2", "e5-e4", "e8-e4", "e8-e5"],
  },
  {
    id: "ws2-s5",
    workstreamId: "workstream-2",
    number: 5,
    nickname: "Sprint 5 – Finance & Trade-In",
    status: "Scheduled",
    storyIds: [],
  },

  // ── Workstream 3: Inventory & Catalog ────────────────────────────────────
  {
    id: "ws3-s1",
    workstreamId: "workstream-3",
    number: 1,
    nickname: "Sprint 1 – Ingestion Pipeline & Elasticsearch",
    status: "Closed",
    storyIds: ["e9-e1", "e9-e2", "e10-e1"],
  },
  {
    id: "ws3-s2",
    workstreamId: "workstream-3",
    number: 2,
    nickname: "Sprint 2 – Catalog Enrichment & Index Schema",
    status: "Closed",
    storyIds: ["e9-e3", "e9-e4", "e9-e5", "e10-e2"],
  },
  {
    id: "ws3-s3",
    workstreamId: "workstream-3",
    number: 3,
    nickname: "Sprint 3 – Bulk Indexing & Kafka Consumer",
    status: "Closed",
    storyIds: ["e10-e3"],
  },
  {
    id: "ws3-s4",
    workstreamId: "workstream-3",
    number: 4,
    nickname: "Sprint 4 – Real-Time Updates & Search Optimisation",
    status: "Active",
    storyIds: ["e10-e4", "e10-e5"],
  },
  {
    id: "ws3-s5",
    workstreamId: "workstream-3",
    number: 5,
    nickname: "Sprint 5 – Image Pipeline & CDN",
    status: "Scheduled",
    storyIds: [],
  },
];
